import { Color, Spacing } from '@persona-id/design-tokens';
import { LoaderIcon } from '@persona-id/design-tokens/icons';
import { AiFillIcon } from '@persona-id/design-tokens/icons/12px';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import RichTextEditor from 'dashboard/components-lib/RichTextEditor';
import { ModuleAISummary } from 'dashboard/modules/AISummary/model';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Stack from 'design-system/components/Stack/Stack';
import { Paragraph } from 'design-system/components/Text';
import { rotate } from 'design-system/variables/keyframes';

const Loader = styled(LoaderIcon)`
  color: ${Color.PrimaryPurple};
  animation: ${rotate} linear 1s infinite;
`;

export interface AISummaryProps {
  module: ModuleAISummary;
  generateSummary: (userPrompt: string) => Promise<string>;
  isPreview?: boolean;
}

const AISummary = ({ module, generateSummary, isPreview = false }: AISummaryProps) => {
  const { 'user-prompt': userPrompt } = module;
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const summary = await generateSummary(userPrompt);
        setSummary(summary);
      } catch (error) {
        setSummary('Failed to generate summary.');
      }
      setIsLoading(false);
    };

    fetchData();
  }, []);

  return (
    <Card>
      <Card.Body>
        {isLoading ? (
          <Stack direction='horizontal' align='center' spacing={Spacing[0]}>
            <Loader />
            <Spacer width={1} height={1} />
            <Paragraph>Generating summary...</Paragraph>
          </Stack>
        ) : (
          <>
            <RichTextEditor defaultValue={isPreview ? userPrompt : summary} readOnly />
            <Spacer height={0.5} />
            <Stack direction='horizontal' align='center' spacing={Spacing[0]}>
              <AiFillIcon color={Color.BorderIconActive} />
              <Spacer width={0.5} height={1} />
              <Paragraph size='xs'>Generated by AI</Paragraph>
            </Stack>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default AISummary;
